import { panelEntities, seriesEntries, metricValue } from '../panelData';
import { sanitizeSfr } from '../../../../Components/constants/constantsAnalytic';
import { detailMetricScales } from './utils/detailMetricScales';
import { sampleCurrentDirections } from '../../../../utils/currentDirections';

export const number = value => value == null || value === '' || !Number.isFinite(Number(value)) ? NaN : Number(value);
export const dateOf = value => {
  const parts = String(value || '').split(/[/-]/).map(Number);
  if (parts.length !== 3) return new Date(NaN);
  const [a, b, c] = parts;
  return a > 999 ? new Date(a, b - 1, c) : new Date(c, b - 1, a);
};
export const dateLabel = date => `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
export const minuteOf = time => {
  const match = String(time || '').match(/(?:T|\s|^)(\d{1,2}):(\d{2})(?::(\d{2}))?/);
  return match ? Number(match[1]) * 60 + Number(match[2]) + Number(match[3] || 0) / 60 : NaN;
};
const val = (value, key) => number(metricValue(value, key));
// Counts behave like v1, which sums only the days it receives: an unreported count
// adds 0. Ratios and SFR stay NaN, and absent days are still flagged `missing`.
const countOf = (value, key) => Number.isFinite(val(value, key)) ? val(value, key) : 0;
// Same SFR the v1 averages use (averageSfrFed): only fed days count, and
// implausible values are recomputed or discarded.
const sfrOf = value => {
  if (!Number.isFinite(val(value, 'sfr_value')) || !(val(value, 'delivered_food') > 0)) return NaN;
  const sfr = sanitizeSfr(value?.snapshot ? { ...value, ...value.snapshot } : value);
  return Number.isFinite(sfr) ? sfr : NaN;
};
const TIER_LABEL = { baja: 'Baja', media: 'Media', alta: 'Alta' };
const naturalEntityOrder = (left, right) => String(left?.name ?? '').localeCompare(
  String(right?.name ?? ''),
  undefined,
  { numeric: true, sensitivity: 'base' },
);

const noteDay = timestamp => {
  const value = String(timestamp || '').trim();
  const dayFirst = value.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})/);
  if (dayFirst) return String(Number(dayFirst[1])).padStart(2, '0') + '/' + String(Number(dayFirst[2])).padStart(2, '0') + '/' + dayFirst[3];
  const iso = value.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  return iso ? String(Number(iso[3])).padStart(2, '0') + '/' + String(Number(iso[2])).padStart(2, '0') + '/' + iso[1] : '';
};

const notesForEntity = (notes, entity) => {
  const ids = new Set([
    entity.id,
    entity.id?.split(':').pop(),
    entity.name,
    ...(entity.node?.note_camera_ids || []),
  ].filter(Boolean).map(String));
  return (Array.isArray(notes) ? notes : []).filter(note =>
    (note?.cam_ids || []).some(cameraId => ids.has(String(cameraId)))
  );
};

export function buildFullData(data, level, dias) {
  // Match the natural ascending cage order used by the V1 selector.
  const entities = panelEntities(data, level).sort(naturalEntityOrder);
  const start = dateOf(dias?.startDate);
  const observed = entities.flatMap(entity => entity.values.map(({ day }) => dateOf(day))).filter(date => Number.isFinite(+date));
  const begin = Number.isFinite(+start) ? start : new Date(Math.min(...observed));
  const count = Math.max(1, Number(dias?.dayRange) || (observed.length ? Math.round((Math.max(...observed) - begin) / 864e5) + 1 : 1));
  const days = Array.from({ length: Math.min(count, 3660) }, (_, index) => { const day = new Date(begin); day.setDate(day.getDate() + index); return dateLabel(day); });
  const tiers = { Baja: [], Media: [], Alta: [] };
  const rows = entities.map(entity => {
    const values = new Map(entity.values.map(({ day, value }) => [dateLabel(dateOf(day)), value]));
    let cum = 0;
    const cells = days.map(day => {
      const value = values.get(day);
      const ppa = countOf(value, 'perdidos_count'); if (Number.isFinite(ppa)) cum += ppa;
      const al = countOf(value, 'alarms_count'), vis = countOf(value, 'visitas_count');
      return { missing: !value, ay: Number(value?.fasts_count) > 0 && !(Number(value?.delivered_food) > 0),
        fasts: countOf(value, 'fasts_count'), al, vis, apv: val(value, 'alarms_by_visits'), apt: val(value, 'alarms_by_ton'),
        ton: val(value, 'biomass') / 1000, kg: countOf(value, 'delivered_food'), sfr: sfrOf(value), ppa, cum,
        summary: value,
      };
    });
    // Tier counts come as numbers, {value, tasas} (compacted) or {alarm, tasas}
    // (area tasa_summary, possibly nested by cage). At center level the backend
    // keeps the rates per tier in a sibling series, alarms_by_tasa_levels.
    const levelsByDay = new Map(seriesEntries(entity.node.alarms_by_tasa_levels)
      .map(({ day, value }) => [dateLabel(dateOf(day)), value || {}]));
    seriesEntries(entity.node.alarms_by_tasa || entity.node.tasa_summary).forEach(({ day, value }) => {
      const date = dateLabel(dateOf(day));
      const levels = levelsByDay.get(date) || {};
      const visit = node => Object.entries(node || {}).forEach(([key, item]) => {
        const label = TIER_LABEL[key];
        if (label) {
          const n = number(typeof item === 'number' ? item : item?.value ?? item?.alarm);
          const listed = Array.isArray(item?.tasas) && item.tasas.length ? item.tasas : levels[key];
          const rates = [...new Set((Array.isArray(listed) ? listed : []).map(number).filter(v => Number.isFinite(v) && v > 0))];
          if (n > 0) tiers[label].push({ date, n, rates, rv: rates.length ? rates.reduce((a, b) => a + b, 0) / rates.length : NaN,
            rate: rates.length ? rates.join(' / ') : 'Sin tasa informada', cages: entity.name });
        } else if (item && typeof item === 'object' && !Array.isArray(item)) visit(item);
      });
      visit(value);
    });
    return {
      name: entity.name,
      id: entity.id,
      node: entity.node,
      cells,
      notes: notesForEntity(data?.notes, entity),
    };
  });
  return { days, rows, tiers, cages: rows.map(row => row.name), empty: !rows.length || !observed.length };
}

const onDay = (series, day) => seriesEntries(series).find(entry => dateLabel(dateOf(entry.day)) === day)?.value || [];
const getMetric = (point, key) => {
  const source = point?.synthetic && point?.tooltipSource ? point.tooltipSource : point;
  const keys = Array.isArray(key) ? key : [key];
  for (const candidate of keys) {
    const value = source?.snapshot?.[candidate] ?? source?.[candidate];
    if (Number.isFinite(number(value))) return number(value);
  }
  return NaN;
};

// V1 passes the complete backend presentation series to Chart.js. Synthetic
// points are intentional interpolation samples and must remain at their presented
// timestamp/value so every V2 representation has the same curve as V1.
const v1Points = (series) => (Array.isArray(series) ? series : [])
  .filter(point => Number.isFinite(minuteOf(point?.time)))
  .slice()
  .sort((left, right) => minuteOf(left.time) - minuteOf(right.time));

// Keep the legacy slots for aggregate views, while daily charts retain the full
// V1 presentation series with its exact timestamps and values.
export function readDetail(row, day, showReaction = false, selectedRateKey = 'tasa') {
  const node = row?.node || {};
  const series = {};
  const pts = Array.from({ length: 181 }, (_, i) => ({ t: 450 + i * 5, act: NaN, oxy: NaN, temp: NaN, cur: NaN, dir: NaN, dep: NaN, tasa: NaN, ppa: NaN, ppaC: NaN }));
  const buckets = Array.from({ length: 181 }, () => ({}));
  const put = (inputPoints, keys, maxKeys = []) => {
    v1Points(inputPoints).forEach(point => {
      const minute = minuteOf(point.time);
      const index = Math.floor((minute - 450) / 5);
      if (!Number.isFinite(minute)) return;
      Object.entries(keys).forEach(([dest, source]) => {
        // V1 plots the series value; the alarm snapshot is a separate,
        // instantaneous measurement and must not replace that plotted value.
        const value = Array.isArray(source) && source[0] === 'value'
          ? number(point.value)
          : getMetric(point, source);
        if (!Number.isFinite(value)) return;
        (series[dest] ||= []).push({ t: minute, value });
        if (index < 0 || index >= pts.length) return;
        (buckets[index][dest] ||= []).push({ value, minute });
      });
    });
    buckets.forEach((bucket, index) => Object.entries(bucket).forEach(([key, samples]) => {
      const values = samples.map(sample => sample.value);
      pts[index][key] = maxKeys.includes(key) ? Math.max(...values) : values.reduce((sum, value) => sum + value, 0) / values.length;
      pts[index][`${key}T`] = samples.reduce((sum, sample) => sum + sample.minute, 0) / samples.length;
    }));
  };
  const activity = Object.entries(node).filter(([key]) => key.startsWith('graph_activity'));
  if (!activity.length && node.activity_data) activity.push(['activity_data', node.activity_data]);
  const sources = activity.map(([, source]) => onDay(source?.data || source, day));
  sources.forEach((points, activityIndex) => put(points, {
    ...(activityIndex === 0 ? { act: ['value', 'activity'] } : {}),
    [`act${activityIndex}`]: ['value', 'activity'],
  }));
  // Compact cameras share water sensors: V1 uses the first reporting source.
  const oxygenSource = sources.find(points => v1Points(points).some(p => Number.isFinite(getMetric(p, ['so2', 'o2'])))) || [];
  const oxygenKey = v1Points(oxygenSource).some(p => Number.isFinite(getMetric(p, 'so2'))) ? 'so2' : 'o2';
  put(oxygenSource, { oxy: oxygenKey });
  ['temp', 'cs', 'cd', 'depth'].forEach((key, index) => {
    const source = sources.find(points => v1Points(points).some(p => Number.isFinite(getMetric(p, key)))) || [];
    put(source, { [['temp', 'cur', 'dir', 'dep'][index]]: key });
  });
  const preferredRateSeries = selectedRateKey === 'tasa_pfm' ? node.graph_feeding_pfm : node.graph_feeding;
  const preferredRatePoints = onDay(preferredRateSeries, day);
  const feedingPoints = preferredRatePoints.length
    ? preferredRatePoints
    : onDay(node.graph_feeding, day).length ? onDay(node.graph_feeding, day) : onDay(node.tasa_data, day);
  put(feedingPoints, { tasa: ['value', selectedRateKey] });
  // graph_perdidos is already cumulative (v1 plots it as is); keep the last reading
  // per slot as ppaC and derive the per-slot increment as ppa.
  put(onDay(node.graph_perdidos, day), { ppaC: ['value', 'perdidos'] }, ['ppaC']);
  let prev = 0; pts.forEach(point => {
    if (!Number.isFinite(point.ppaC)) return;
    point.ppa = Math.max(0, point.ppaC - prev); prev = Math.max(prev, point.ppaC);
  });
  const alarms = (onDay(node.alarms_data, day) || []).map(alarm => {
    const min = minuteOf(alarm.start || alarm.start_time || alarm.time);
    return { i: Math.max(0, Math.min(180, Math.floor((min - 450) / 5))), sec: min * 60,
      dur: number(alarm.duration), dmax: number(alarm.detectados_max ?? alarm.max_value ?? alarm.max),
      dtot: number(alarm.detectados_total ?? alarm.total_value ?? alarm.total),
      snapshot: alarm.snapshot, preContext: alarm.pre_alarm_context,
      att: showReaction ? alarm.atendido : null, react: showReaction && alarm.atendido === true ? number(alarm.latency_sec) : null,
    };
  }).filter(alarm => Number.isFinite(alarm.sec));
  const notes = (row?.notes || []).filter(note => noteDay(note?.timestamp) === day).map(note => ({
    minute: minuteOf(note.timestamp),
    text: String(note.text ?? note.note ?? note.description ?? '').trim() || 'Sin texto',
    raw: note,
  })).filter(note => Number.isFinite(note.minute));
  Object.values(series).forEach(samples => samples.sort((a, b) => a.t - b.t));
  return { pts, alarms, notes, series, oxygenKey, selectedRateKey,
    activityMeta: activity.map(([key, series], index) => ({ key: `act${index}`, name: series?.name || key })),
    depthStrata: seriesEntries(node.depth_strata).find(entry => dateLabel(dateOf(entry.day)) === day)?.value };
}

export function detailGeometry(raw) {
  const { pts, alarms } = raw;
  const samples = key => raw.series?.[key] || (raw.series ? [] : pts.filter(p => Number.isFinite(p[key])).map(p => ({ t: p[`${key}T`] ?? p.t, value: p[key] })));
  const ext = key => { const values = samples(key).map(p => p.value); return values.length ? [Math.min(...values), Math.max(...values)] : [NaN, NaN]; };
  const X = minute => (minute - 450) / 900 * 600;
  const Y = (value, low, high) => 95 - (value - low) / (high - low || 1) * 90;
  const measuredTimes = [
    ...(raw.series ? Object.values(raw.series).flat() : []).map(p => p.t),
    ...(raw.notes || []).map(note => note.minute),
  ];
  const timeMin = measuredTimes.length ? Math.min(...measuredTimes) : 450;
  const timeMaxRaw = measuredTimes.length ? Math.max(...measuredTimes) : 1350;
  const timeMax = Math.max(timeMin + 5, timeMaxRaw);
  const chartLeft = X(timeMin), chartRight = X(timeMax);
  // V1 connects every point in the complete backend presentation series, even
  // across long reporting gaps. Splitting by elapsed slots makes V2 incomplete.
  const lineSegments = key => {
    const current = samples(key).map(point => ({ x: X(point.t), value: point.value }));
    return current.length ? [current] : [];
  };
  // Match the weighted cubic control points used by Chart.js with the same
  // dataset tension as V1. Cap controls to the actual day's chart area rather
  // than the legacy 07:30–22:30 coordinates, which made late curves loop back.
  const smoothLine = (key, low, high, tension = 0.35) => lineSegments(key).map(segment => {
    const points = segment.map(point => ({ x: point.x, y: Y(point.value, low, high) }));
    if (points.length < 3) return points.map((point, index) => `${index ? 'L' : 'M'}${point.x.toFixed(2)} ${point.y.toFixed(2)}`).join(' ');
    const controls = points.map((point, index) => {
      const previous = points[Math.max(0, index - 1)], next = points[Math.min(points.length - 1, index + 1)];
      const before = Math.hypot(point.x - previous.x, point.y - previous.y);
      const after = Math.hypot(next.x - point.x, next.y - point.y);
      const total = before + after || 1;
      return {
        previous: {
          x: Math.max(chartLeft, Math.min(chartRight, point.x - (next.x - previous.x) * tension * before / total)),
          y: Math.max(0, Math.min(100, point.y - (next.y - previous.y) * tension * before / total))
        },
        next: {
          x: Math.max(chartLeft, Math.min(chartRight, point.x + (next.x - previous.x) * tension * after / total)),
          y: Math.max(0, Math.min(100, point.y + (next.y - previous.y) * tension * after / total))
        }
      };
    });
    let path = `M${points[0].x.toFixed(2)} ${points[0].y.toFixed(2)}`;
    for (let index = 1; index < points.length; index++) {
      const c1 = controls[index - 1].next, c2 = controls[index].previous, point = points[index];
      path += ` C${c1.x.toFixed(2)} ${c1.y.toFixed(2)} ${c2.x.toFixed(2)} ${c2.y.toFixed(2)} ${point.x.toFixed(2)} ${point.y.toFixed(2)}`;
    }
    return path;
  }).join(' ') || 'M0 0';
  const steppedArea = (key, low, high) => lineSegments(key).map(segment => {
    if (!segment.length) return '';
    const first = segment[0], last = segment[segment.length - 1];
    let path = `M${first.x.toFixed(2)} 100 L${first.x.toFixed(2)} ${Y(first.value, low, high).toFixed(2)}`;
    for (let i = 1; i < segment.length; i++) {
      const point = segment[i];
      path += ` H${point.x.toFixed(2)} V${Y(point.value, low, high).toFixed(2)}`;
    }
    return `${path} L${last.x.toFixed(2)} 100 Z`;
  }).join(' ') || 'M0 0';
  const activityKeys = [...new Set((raw.series ? Object.keys(raw.series) : pts.flatMap(Object.keys)).filter(key => /^act\d+$/.test(key)))]
    .sort((left, right) => Number(left.slice(3)) - Number(right.slice(3)));
  const activityValues = activityKeys.flatMap(key => samples(key).map(p => p.value));
  const hasActivity = activityValues.length > 0 || samples('act').length > 0;
  const hasRate = samples('tasa').length > 0;
  const hasPpa = samples('ppaC').length > 0;
  const hasOxygen = samples('oxy').length > 0;
  const hasTemperature = samples('temp').length > 0;
  const hasCurrent = samples('cur').length > 0;
  const hasDirection = samples('dir').length > 0;
  const hasDepth = samples('dep').length > 0;
  const [oLo, oHi] = ext('oxy'), [tLo, tHi] = ext('temp'),
    [aLo, aHi] = activityValues.length ? [Math.min(...activityValues), Math.max(...activityValues)] : ext('act'),
    [cLo, cHi] = ext('cur'), [dLo, dHi] = ext('dep');
  const scales = detailMetricScales(Object.fromEntries(['act', 'tasa', 'ppaC', 'oxy', 'temp', 'cur', 'dep'].map(key => [key, samples(key)])), raw.oxygenKey);
  const oxygenUnit = scales.oxy.unit;
  const oxygenLow = scales.oxy.lo, oxygenHigh = scales.oxy.hi;
  const temperatureLow = scales.temp.lo, temperatureHigh = scales.temp.hi;
  const curMax = scales.cur.hi;
  const rateMax = scales.tasa.hi;
  const card = d => ['N', 'NE', 'E', 'SE', 'S', 'SO', 'O', 'NO'][Math.round(d / 45) % 8] || '—';
  const sectors = Array(8).fill(0); pts.forEach(p => { if (Number.isFinite(p.dir) && Number.isFinite(p.cur)) sectors[Math.round(p.dir / 45) % 8] += p.cur; });
  const peak = Math.max(...sectors, 1), dominant = sectors.indexOf(Math.max(...sectors));
  const ticks = (values, low, high, digits = 0) => values.filter(Number.isFinite).map(value => ({ top: `${Y(value, low, high)}%`, lt: `${Math.max(7, Math.min(93, Y(value, low, high)))}%`, lab: value.toFixed(digits) }));
  const axis = key => { const scale = scales[key]; return ticks(scale.values, scale.lo, scale.hi, scale.digits); };
  const formatRange = (low, high, unit, digits = 1) => Number.isFinite(low) ? `${low.toFixed(digits)}–${high.toFixed(digits)} ${unit}` : 'Sin datos';
  const pMax = Math.max(1, ...pts.map(p => p.ppa).filter(Number.isFinite));
  const directionSamples = sampleCurrentDirections(samples('dir').map(point => ({ x: point.t * 60000, cd: point.value })))
    .map(point => ({ t: point.x / 60000, value: point.cd }));
  return {
    ...raw, scales, card, curMax, timeMin, timeMax, oxygenUnit, directionSamples,
    visibility: {
      activity: hasActivity,
      rate: hasRate,
      ppa: hasPpa,
      primary: hasActivity || hasRate || hasPpa,
      oxygen: hasOxygen,
      temperature: hasTemperature,
      environment: hasOxygen || hasTemperature,
      current: hasCurrent,
      direction: hasDirection,
      currentBlock: hasCurrent || hasDirection,
      depth: hasDepth,
    },
    activities: activityKeys.map((key, index) => ({
      key,
      name: raw.activityMeta?.find(item => item.key === key)?.name || `Actividad ${index + 1}`,
      path: smoothLine(key, 0, 100),
      color: ['#00FF99', '#00CC66', '#00994D', '#66FFB2', '#33CC99'][index % 5],
    })),
    arrows: pts.flatMap((p, i) => i % 6 === 0 && Number.isFinite(p.dir) ? [{ left: `${((p.dirT ?? p.t) - 450) / 900 * 100}%`, rot: String(p.dir), op: '0.8', size: '12' }] : []),
    spans: alarms.map(a => `M${(a.sec / 60 - 450) / 900 * 600} 0 h${Math.max(1, (a.dur || 0) / 60 / 900 * 600)} V100 H${(a.sec / 60 - 450) / 900 * 600} Z`).join(' ') || 'M0 0',
    dom: sectors.some(Boolean) ? `${card(dominant * 45)} · ${dominant * 45}°` : 'Sin datos',
    rose: sectors.map((value, i) => ({ deg: i * 45, len: String(value / peak * 30), op: String(value / peak) })),
    act: smoothLine('act', 0, 100), oxy: smoothLine('oxy', oxygenLow, oxygenHigh), temp: smoothLine('temp', temperatureLow, temperatureHigh),
    cur: smoothLine('cur', 0, curMax), curArea: 'M0 0', dep: smoothLine('dep', scales.dep.lo, scales.dep.hi), tasa: steppedArea('tasa', 0, rateMax),
    ppaCum: smoothLine('ppaC', scales.ppa.lo, scales.ppa.hi, 0.25),
    ppa: pts.flatMap(p => p.ppa > 0 ? [`M${X(p.ppaCT ?? p.t)} 100 V${100 - p.ppa / pMax * 45} h2 V100 Z`] : []).join(' ') || 'M0 0', marks: 'M0 0',
    axA: axis('act'), axT: axis('tasa'), axP: axis('ppa'), axO: axis('oxy'),
    axTe: axis('temp'), axC: axis('cur'), axD: axis('dep'),
    axPrimary: axis(hasActivity ? 'act' : hasRate ? 'tasa' : 'ppa'),
    axEnvironment: axis(hasOxygen ? 'oxy' : 'temp'),
    actRange: formatRange(aLo, aHi, '%'), oxyRange: formatRange(oLo, oHi, oxygenUnit), tempRange: formatRange(tLo, tHi, '°C', 2),
    curRange: formatRange(cLo, cHi, 'cm/s'), depRange: formatRange(dLo, dHi, 'm'),
    summary: `${alarms.length} eventos de alarma · ${pts.some(p => ['act', 'tasa', 'oxy', 'temp', 'cur', 'dep', 'ppaC'].some(k => Number.isFinite(p[k]))) ? 'Datos horarios disponibles' : 'Sin datos horarios para esta selección'}`,
  };
}
